import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from '@material-ui/core';
import { Publish } from '@material-ui/icons';
import { Actions as Snacks } from '../../../store/ducks/snacks';
import { buildSelectionFromCsv } from '../visualization/imageCsv';

/**
 * @function ImportDatesButton
 * @description Lets the user restore an image selection from the date CSV exported by the image
 * chooser cards. The file replaces the current selection: images listed in it are checked and
 * every other one is unchecked, so the result is exactly what the file describes.
 * @param {Object} props - Component properties.
 * @param {Array<Object>} props.images - Images currently displayed by the table.
 * @param {Function} props.onImport - Receives the selection map built from the file.
 * @param {boolean} props.disabled - Disables the button.
 * **Called by:
 * @see {ImageListRefiner} - shoreline/shoreR, mepbay and intertidal variants
 * @see {ShoreSImageSelector}
 * @returns {JSX.Element} The import button wrapping a hidden file input.
 */
const ImportDatesButton = ({ images = [], onImport, disabled = false }) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();

  const notify = (action, message) => {
    // Notifier keeps a module-level list of already displayed keys, so a fresh key per import
    // is what makes consecutive imports each show their own snack.
    dispatch(action(`image-csv-import-${Date.now()}`, message));
  };

  const handleChange = (event) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e) => {
      const { selection, matched, total } = buildSelectionFromCsv(String(e.target.result), images);

      if (total === 0) {
        notify(Snacks.warn, t('forms.acquisition.4.importInvalid'));
      } else {
        onImport(selection);
        if (matched === 0) notify(Snacks.warn, t('forms.acquisition.4.importEmpty'));
        else notify(Snacks.success, t('forms.acquisition.4.importResult', { matched, total }));
      }

      input.value = '';
    };

    reader.onerror = () => {
      notify(Snacks.error, t('forms.acquisition.4.importError'));
      input.value = '';
    };

    reader.readAsText(file);
  };

  return (
    <Button variant="outlined" component="label" startIcon={<Publish />} disabled={disabled}>
      {t('forms.acquisition.4.importDates')}
      <input type="file" accept=".csv,text/csv" hidden onChange={handleChange} />
    </Button>
  );
};

export default ImportDatesButton;
